import { useEffect, useRef, useCallback } from "react";
import { BankPaymentSession } from "@/types";

declare global {
  interface Window {
    Checkout?: {
      configure: (config: { session: { id: string } }) => void;
      showPaymentPage: () => void;
    };
  }
}

interface UseBankCheckoutProps {
  session: BankPaymentSession | null;
  onError: (error: string) => void;
}

const { VITE_ENV } = import.meta.env;

export const useBankCheckout = ({ session, onError }: UseBankCheckoutProps) => {
  const sessionInitializedRef = useRef<string | null>(null);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize error handler to prevent unnecessary effect re-runs
  const handleError = useCallback((errorMsg: string) => {
    console.error("Bank checkout error:", errorMsg);
    onError(errorMsg);
  }, [onError]);

  useEffect(() => {
    console.log('🔄 useBankCheckout effect triggered:', { 
      hasSession: !!session, 
      sessionId: session?.id,
      currentInitialized: sessionInitializedRef.current
    });
    
    if (!session?.id) {
      console.log('⏹️ No session available, exiting useBankCheckout');
      sessionInitializedRef.current = null;
      // Clear any pending initialization
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
        initializationTimeoutRef.current = null;
      }
      return;
    }

    // Prevent re-initialization of the same session
    if (sessionInitializedRef.current === session.id) {
      console.log('⚠️ Session already initialized, skipping:', session.id);
      return;
    }

    console.log('🔧 Initializing Bank Alfalah checkout for session:', session.id);

    const initializeCheckout = () => {
      if (!window.Checkout) {
        console.error("❌ window.Checkout not available");
        handleError("Payment system not available. Please refresh the page.");
        return;
      }

      if (!session?.id) {
        console.error("❌ No session ID available");
        handleError("Payment session not available");
        return;
      }

      try {
        console.log("✅ Configuring Bank Alfalah checkout with session:", session.id);
        
        // Mark this session as initialized
        sessionInitializedRef.current = session.id;
        
        // Configure the checkout
        window.Checkout.configure({
          session: { id: session.id },
        });
        
        console.log("✅ Configuration complete, showing payment page...");
        
        // Show payment page immediately
        window.Checkout.showPaymentPage();
        
        console.log("🚀 Bank Alfalah payment page should now be visible");
        
      } catch (error) {
        console.error("❌ Error during checkout initialization:", error);
        sessionInitializedRef.current = null;
        handleError("Failed to initialize payment checkout. Please try again.");
      }
    };

    // Check if Checkout is available (script loaded in HTML)
    if (window.Checkout) {
      console.log("✅ window.Checkout available, initializing immediately...");
      initializeCheckout();
    } else {
      console.log("⏳ window.Checkout not available yet, waiting...");
      
      // Wait for script to load with timeout
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds total
      
      initializationTimeoutRef.current = setInterval(() => {
        attempts++;
        
        if (window.Checkout) {
          console.log("✅ window.Checkout became available after", attempts * 100, "ms");
          if (initializationTimeoutRef.current) {
            clearInterval(initializationTimeoutRef.current);
            initializationTimeoutRef.current = null;
          }
          initializeCheckout();
        } else if (attempts >= maxAttempts) {
          console.error("❌ Timeout waiting for window.Checkout");
          if (initializationTimeoutRef.current) {
            clearInterval(initializationTimeoutRef.current);
            initializationTimeoutRef.current = null;
          }
          handleError("Payment system failed to load. Please refresh the page.");
        }
      }, 100) as NodeJS.Timeout;
    }

    // Cleanup function
    return () => {
      if (initializationTimeoutRef.current) {
        clearInterval(initializationTimeoutRef.current);
        initializationTimeoutRef.current = null;
      }
    };
  }, [session, handleError]);
};

export default useBankCheckout;
